import { DateTime } from 'luxon';

/**
 * Aritmética e regras de exibição da cobrança.
 *
 * Fora do componente porque são as contas que decidem o que a tela mostra — quanto ainda
 * pode ser estornado, se o boleto já venceu, se um painel tem conteúdo ou é só um retângulo
 * cinza dizendo que não tem nada.
 *
 * Como em `movementBreakdown`, nada aqui formata dinheiro: `maskCurrency` lê uma flag global
 * mutável e congelaria se o resultado fosse memoizado.
 */

/** Espelha `ItemChargeStatus`, sem importar o enum — `types/Charge` arrasta a tela inteira. */
export const REFUND_COMMITTED = ['FINISHED', 'PENDING'];

export interface RefundLike {
  status?: string | null;
  amount?: string | number | null;
}

export const toAmount = (value?: string | number | null): number => {
  if (value === null || value === undefined || value === '') return 0;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Devolução em processamento conta junto com a concluída: o dinheiro já saiu do que se pode
 * estornar de novo, e ignorá-la deixaria estornar duas vezes o mesmo valor.
 */
export const refundedTotal = (refunds?: RefundLike[]): number =>
  (refunds ?? [])
    .filter((refund) => REFUND_COMMITTED.includes(refund?.status ?? ''))
    .reduce((total, refund) => total + toAmount(refund?.amount), 0);

export const hasPendingRefund = (refunds?: RefundLike[]): boolean =>
  (refunds ?? []).some((refund) => refund?.status === 'PENDING');

/** Nunca negativo: devolução maior que o valor viraria um limite de estorno inválido. */
export const remainingRefundable = (amount?: string | number | null, refunds?: RefundLike[]): number => {
  const remaining = toAmount(amount) - refundedTotal(refunds);
  return remaining > 0 ? Math.round(remaining * 100) / 100 : 0;
};

export const itensTotal = (itens?: { amount?: string | number | null }[]): number =>
  (itens ?? []).reduce((total, item) => total + toAmount(item?.amount), 0);

/**
 * Cobrança vencida: a data de expiração já passou e ela não foi paga.
 *
 * Sem o recorte de status, uma cobrança paga ontem com expiração de ontem apareceria como
 * vencida — o que é verdade no relógio e mentira no negócio.
 */
export const isExpired = (expireAt?: string | null, status?: string | null, nowMillis = Date.now()): boolean => {
  if (!expireAt || status === 'PAID' || status === 'CANCELED') return false;

  const expiry = DateTime.fromISO(expireAt);
  return expiry.isValid && expiry.toMillis() < nowMillis;
};
